import * as React from "react";
import { Fab, Typography, makeStyles } from "@material-ui/core";
import * as qs from "query-string";
import { useTranslation } from "react-i18next";
import { Link, Avatar } from "src/common/components";
import { IconArrowDropDown } from "src/common/icons";
import { IMyPeer, ITalk, OpenTasksSubmissionStatus, QuestionnaireType, SubmissionStatus } from "src/common/types";
import { dateWithoutTime, javaDateToJS, getFullName } from "src/helpers";
import DateStatus from "../DateStatus";
import Status from "../EmployeeCard/status";
import OpenTasks from "../OpenTasks";

const useStyles = makeStyles((theme: any) => ({
  arrow: {
    display: "inline-block",
    fontSize: "1.35rem",
    lineHeight: "1",
    transform: "rotate(360deg) scale(0.9,1.35)",
    transition: "transform 0.2s linear",
    verticalAlign: "middle" as "middle"
  },
  avatarContainer: {
    padding: "0 8px 0 16px",
    width: "66px"
  },
  cycleDetailsContainer: {
    borderBottom: "1px solid #e6e6e6",
    display: "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between",
    lineHeight: "30px",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "initial",
      lineHeight: "60px"
    }
  },
  cycleNameContainer: {
    display: "block" as "block",
    padding: "0 8px 0 16px",
    width: "77%"
  },
  dateWrapper: {
    color: "#707070",
    display: "none",
    minWidth: 110,
    padding: "0 8px",
    width: 110,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  detailsContainer: {
    alignItem: "center" as "center",
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    justifyContent: "space-between" as "space-between",
    lineHeight: "42px",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      lineHeight: "60px"
    }
  },
  expandButton: {
    background: "none",
    boxShadow: "none"
  },
  expandButtonLabel: {
    height: "100%"
  },
  expandLabel: {
    alignItems: "center" as "center",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    height: "40px",
    justifyContent: "center" as "center",
    width: "40px"
  },
  expandWrapper: {
    minWidth: 54,
    padding: "0 16px 0 8px",
    width: 54
  },
  fullNameContainer: {
    flexGrow: 1,
    padding: "0 8px"
  },
  fullNameLabel: {
    cursor: "pointer"
  },
  mainContainer: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 4,
    boxShadow: "0 2px 4px 0 rgba(219, 219, 219, 0.5)",
    display: "flex",
    flexWrap: "wrap" as "wrap",
    margin: "0 0 8px",
    overflow: "hidden"
  },
  peerCycleContainer: {
    display: "block",
    overflow: "hidden" as "hidden",
    position: "relative" as "relative",
    transition: ".3s max-height ease-out",
    width: "100%"
  },
  peerCycleContainerExpandable: { maxHeight: 0 },
  statusWrapper: {
    display: "block",
    lineHeight: 1,
    padding: "0 8px",
    [theme.breakpoints.up("md")]: {
      lineHeight: "inherit" as "inherit",
      minWidth: 183,
      width: 183
    }
  },
  title: {
    display: "inline-block",
    lineHeight: 1,
    overflowWrap: "break-word" as "break-word",
    verticalAlign: "middle" as "middle",
    wordBreak: "break-word" as "break-word"
  },
  toggler: {
    "&:checked ~ $detailsContainer $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    },
    "&:checked ~ $peerCycleContainerExpandable": {
      maxHeight: "99999px",
      transition: "1s max-height ease-in"
    },
    display: "none" as "none"
  }
}));

interface IOwnProps {
  currentTab: string;
  inHistory: boolean;
}

type Props = IOwnProps & IMyPeer;

const Peer = ({ currentTab, firstName, middleName, lastName, photoBase64, id, inHistory, talks }: Props) => {
  const classes = useStyles();
  const {
    i18n: { language }
  } = useTranslation();
  const fullName = getFullName([firstName, middleName, lastName]);

  const firstTalk = talks[0] as ITalk;
  let { submissionDueDate: firstTalkSubmissionDueDate } = firstTalk && firstTalk.talkQuestionnaires[0];

  const { submissionStatus: firstTalkSubmissionStatus } = firstTalk && firstTalk.talkQuestionnaires[0];

  firstTalkSubmissionDueDate = javaDateToJS(firstTalkSubmissionDueDate);

  const currentDate = new Date();
  const firstTalkSubmissionDueDatePassed = dateWithoutTime(currentDate) > dateWithoutTime(firstTalkSubmissionDueDate);

  const firstTalkSubmissionDueDateType =
    firstTalkSubmissionDueDatePassed && OpenTasksSubmissionStatus.includes(firstTalkSubmissionStatus)
      ? "warning"
      : firstTalkSubmissionDueDatePassed && firstTalkSubmissionStatus === SubmissionStatus.Submitted
      ? "success"
      : "normal";

  const talkCheckboxId = `peer:${id}`;
  const showToggler = currentTab === "EXPIRED";
  const peerCycleContainerClasses = [classes.peerCycleContainer];
  if (showToggler) {
    peerCycleContainerClasses.push(classes.peerCycleContainerExpandable);
  }

  const openTasksCount = React.useMemo(() => {
    if (inHistory) {
      return 0;
    } else {
      return talks.filter(({ talkQuestionnaires }) => {
        return talkQuestionnaires.some(({ submissionStatus, questionnaireType }) => {
          return questionnaireType === QuestionnaireType.Peer && OpenTasksSubmissionStatus.includes(submissionStatus);
        });
      }).length;
    }
  }, [talks, inHistory]);

  return (
    <div className={classes.mainContainer}>
      {showToggler && <input className={classes.toggler} id={talkCheckboxId} defaultChecked={false} type="checkbox" />}
      <div className={classes.detailsContainer}>
        <div className={classes.avatarContainer}>
          <OpenTasks count={openTasksCount}>
            <Avatar type="small" fullName={fullName} avatar={photoBase64} />
          </OpenTasks>
        </div>
        <div className={classes.fullNameContainer}>
          <label className={classes.fullNameLabel} htmlFor={talkCheckboxId}>
            <Typography className={classes.title} display="inline" color="primary" variant="subtitle2">
              {fullName}
            </Typography>
          </label>
        </div>
        {showToggler && (
          <>
            <div className={classes.statusWrapper}>
              <Status type="questionnaire" status={firstTalkSubmissionStatus} />
            </div>
            <div className={classes.dateWrapper}>
              <DateStatus type={firstTalkSubmissionDueDateType} date={firstTalkSubmissionDueDate} />
            </div>
            <div className={classes.expandWrapper}>
              <Fab
                classes={{ label: classes.expandButtonLabel }}
                className={classes.expandButton}
                size="small"
                color="default"
              >
                <label className={classes.expandLabel} htmlFor={talkCheckboxId}>
                  <Typography color="primary" className={classes.arrow}>
                    <IconArrowDropDown />
                  </Typography>
                </label>
              </Fab>
            </div>
          </>
        )}
      </div>
      <div className={peerCycleContainerClasses.join(" ")}>
        {talks.map(({ id: talkId, cycle: { name: cycleName }, talkQuestionnaires }) => {
          if (talkQuestionnaires && talkQuestionnaires.length) {
            return talkQuestionnaires.map(
              ({
                id: questionnaireId,
                submissionDate: questionnaireSubmissionDate,
                submissionDueDate: questionnaireSubmissionDueDate,
                submissionStatus: questionnaireSubmissionStatus,
                questionnaireTemplateConfig
              }) => {
                questionnaireSubmissionDate = questionnaireSubmissionDate
                  ? javaDateToJS(questionnaireSubmissionDate)
                  : null;
                questionnaireSubmissionDueDate = javaDateToJS(questionnaireSubmissionDueDate);

                const submissionDueDatePassed =
                  dateWithoutTime(currentDate) > dateWithoutTime(questionnaireSubmissionDueDate);

                const submissionDueDateType =
                  submissionDueDatePassed && OpenTasksSubmissionStatus.includes(questionnaireSubmissionStatus)
                    ? "warning"
                    : submissionDueDatePassed && questionnaireSubmissionStatus === SubmissionStatus.Submitted
                    ? "success"
                    : "normal";

                const { supportedLanguages = null } = questionnaireTemplateConfig
                  ? JSON.parse(questionnaireTemplateConfig)
                  : {};

                return (
                  <div key={talkId} className={classes.cycleDetailsContainer}>
                    <div className={classes.cycleNameContainer}>
                      <Link
                        to={`/${language}/dashboard/my-peers/my-peer-questionnaire/${questionnaireId}?${btoa(
                          qs.stringify(
                            { supported: [supportedLanguages] },
                            {
                              arrayFormat: "comma"
                            }
                          )
                        )}`}
                      >
                        {cycleName}
                      </Link>
                    </div>
                    <div className={classes.statusWrapper}>
                      <Status type="questionnaire" status={questionnaireSubmissionStatus} />
                    </div>
                    <div className={classes.dateWrapper}>
                      <DateStatus type={submissionDueDateType} date={questionnaireSubmissionDueDate} />
                    </div>
                    <div className={classes.expandWrapper} />
                  </div>
                );
              }
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default Peer;
